import {
  getGetV7AltibaseInstanceSqlSqlTextByInstanceIdAndSqlIdQueryKey,
  getV7AltibaseInstanceSqlSqlTextByInstanceIdAndSqlId,
} from '@/api-client/altibase/queries/altibase-controller/altibase-controller';
import { DB_TYPE } from '@/common/define/common.define';
import { FRAME_NAMES } from '@/common/define/apiTrace.define';
import { formatTimeRange } from '@/database/utils/format.utils';
import { getAltibaseSqlDetailTitle } from '@/database/components/sqlDetail/common/utils';
import { DATABASE_DETAIL_TYPE, DOCUMENT_TEXT_TYPE } from '@/database/adapter/openDetail.define';
import type {
  DetailParams,
  DocumentTextParams,
  ParameterDetailParams,
  SessionDetailParams,
  SqlDetailParams,
} from '../types';
import { formatName, mergeDetailInfo, parseSchema } from './utils';
import { getRequiredMap } from '@/common/utils/core/detailFieldMeta.utils';
import {
  sessionDetailSchema,
  sqlDetailSchema,
  parameterDetailSchema,
  replicationDetailSchema,
  replicationObjectDetailSchema,
  documentTextSchema,
  type SessionDetailSchema,
  type AltibaseSessionDetailAddition,
  type SqlDetailSchema,
  type AltibaseSqlDetailAddition,
  type ParameterDetailSchema,
  type AltibaseParameterDetailAddition,
  type ReplicationDetailSchema,
  type AltibaseReplicationDetailAddition,
  type ReplicationObjectDetailSchema,
  type AltibaseReplicationObjectDetailAddition,
  type DocumentTextSchema,
} from './altibase.schema';

export const getAltibaseSessionDetailAddition = ({
  detailInfo,
  fromTime,
  toTime,
  isOpenedFromPA,
  init,
  instanceId,
}: SessionDetailParams): {
  name: string;
  addition: AltibaseSessionDetailAddition;
} => {
  const { data } = parseSchema<SessionDetailSchema>(
    sessionDetailSchema,
    mergeDetailInfo(detailInfo, instanceId, { fromTime, toTime, isOpenedFromPA, init }),
    DATABASE_DETAIL_TYPE.SESSION,
  );

  return {
    name: formatName(data.sid),
    addition: {
      dbType: DB_TYPE.ALTIBASE,
      instanceId: data.instanceId,
      fromTime: data.fromTime,
      toTime: data.toTime,
      sid: data.sid,
      stmtId: data.stmtId,
      loginTime: data.loginTime,
      clientPid: data.clientPid,
      isOpenedFromPA: data.isOpenedFromPA,
      init: data.init,
    },
  };
};

export const getAltibaseSqlDetailAddition = ({
  detailInfo,
  fromTime,
  toTime,
  instanceId,
}: SqlDetailParams): {
  name: string;
  addition: AltibaseSqlDetailAddition;
} => {
  const { data } = parseSchema<SqlDetailSchema>(
    sqlDetailSchema,
    mergeDetailInfo(detailInfo, instanceId, { fromTime, toTime }),
    DATABASE_DETAIL_TYPE.SQL,
  );

  return {
    name: getAltibaseSqlDetailTitle(detailInfo),
    addition: {
      dbType: DB_TYPE.ALTIBASE,
      instanceId: data.instanceId,
      fromTime: data.fromTime,
      toTime: data.toTime,
      sqlId: data.sqlId,
      sqlTextHashId: data.sqlTextHashId,
      stmtId: data.stmtId,
    },
  };
};

export const getAltibaseParameterDetailAddition = ({
  detailInfo,
  fromTime,
  toTime,
  instanceId,
}: ParameterDetailParams): {
  name: string;
  addition: AltibaseParameterDetailAddition;
} => {
  const { data } = parseSchema<ParameterDetailSchema>(
    parameterDetailSchema,
    mergeDetailInfo(detailInfo, instanceId, { fromTime, toTime }),
    DATABASE_DETAIL_TYPE.PARAMETER,
  );

  return {
    name: formatName(data.parameterName),
    addition: {
      dbType: DB_TYPE.ALTIBASE,
      instanceId: data.instanceId,
      fromTime: data.fromTime,
      toTime: data.toTime,
    },
  };
};

export const getAltibaseReplicationDetailAddition = ({
  detailInfo,
  fromTime,
  toTime,
  instanceId,
}: DetailParams): {
  name: string;
  addition: AltibaseReplicationDetailAddition;
} => {
  const { data } = parseSchema<ReplicationDetailSchema>(
    replicationDetailSchema,
    mergeDetailInfo(detailInfo, instanceId, { fromTime, toTime }),
    DATABASE_DETAIL_TYPE.REPLICATION,
  );

  return {
    name: formatName(data.replicationName),
    addition: {
      dbType: DB_TYPE.ALTIBASE,
      instanceId: data.instanceId,
      replicationName: data.replicationName,
      fromTime: data.fromTime,
      toTime: data.toTime,
    },
  };
};

export const getAltibaseReplicationObjectDetailAddition = ({
  detailInfo,
  fromTime,
  toTime,
  instanceId,
}: DetailParams): { name: string; addition: AltibaseReplicationObjectDetailAddition } => {
  const { data } = parseSchema<ReplicationObjectDetailSchema>(
    replicationObjectDetailSchema,
    mergeDetailInfo(detailInfo, instanceId, { fromTime, toTime }),
    DATABASE_DETAIL_TYPE.REPLICATION_OBJECT,
  );

  return {
    name: formatName(data.replicationName),
    addition: {
      dbType: DB_TYPE.ALTIBASE,
      instanceId: data.instanceId,
      objectName: data.objectName,
      fromTime: data.fromTime,
      toTime: data.toTime,
      seriesColor: data.seriesColor ?? {},
    },
  };
};

export const getAltibaseDocumentTextParams = ({
  detailInfo,
  fromTime,
  toTime,
  instanceId,
  period,
  useFieldValidation,
}: DocumentTextParams) => {
  const formattedTime = formatTimeRange({ fromTime, toTime });
  const timeInfo = period ? { period } : formattedTime;

  if (!useFieldValidation) {
    return {
      instanceId,
      ...timeInfo,
      sqlId: detailInfo.sqlId,
      sqlTextHashId: detailInfo.sqlTextHashId,
    };
  }

  const { data } = parseSchema<DocumentTextSchema>(
    documentTextSchema,
    mergeDetailInfo(detailInfo, instanceId),
    DOCUMENT_TEXT_TYPE,
  );

  return {
    instanceId: data.instanceId,
    ...timeInfo,
    sqlId: data.sqlId,
    sqlTextHashId: data.sqlTextHashId,
  };
};

export const fetchAltibaseDocumentText = async ({
  frameName,
  abortController,
  ...rest
}: DocumentTextParams) => {
  const params = getAltibaseDocumentTextParams({ ...rest, useFieldValidation: true });

  const {
    data: { data = [] },
  } = await getV7AltibaseInstanceSqlSqlTextByInstanceIdAndSqlId(
    params.instanceId!,
    params.sqlId,
    { sqlTextHashId: params.sqlTextHashId },
    { params: { frameName: frameName || FRAME_NAMES.DOCUMENT_WINDOW.FULL_TEXT } },
    abortController?.signal,
  );

  return data[0]?.literalQuery || data[0]?.normalizedQuery || null;
};

export const getAltibaseDocumentTextQueryKey = (params: DocumentTextParams | null) => {
  if (!params) {
    return getGetV7AltibaseInstanceSqlSqlTextByInstanceIdAndSqlIdQueryKey();
  }

  const convertedParams = getAltibaseDocumentTextParams({ ...params, useFieldValidation: false });

  return getGetV7AltibaseInstanceSqlSqlTextByInstanceIdAndSqlIdQueryKey(
    convertedParams.instanceId,
    convertedParams.sqlId,
    convertedParams,
  );
};

export const altibaseSqlDetailRequiredMap = getRequiredMap(sqlDetailSchema);
export const altibaseSessionDetailRequiredMap = getRequiredMap(sessionDetailSchema);
export const altibaseReplicationDetailRequiredMap = getRequiredMap(replicationDetailSchema);
export const altibaseDocumentTextRequiredMap = getRequiredMap(documentTextSchema);
